// MCHD Room Scheduler - Blocks API Client
// Frontend API client for block operations


const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7991/api/v1';

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Create headers for API requests
 * @param token - Authentication token
 * @returns Headers object
 */
function createHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface Block {
  id: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BlockWithStats extends Block {
  assignmentCount: number;
}

export interface CreateBlockData {
  name: string;
  description?: string;
  roomId: string;
  startDate: string;
  endDate: string;
  isActive?: boolean;
}

export interface UpdateBlockData {
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
}

export interface GetBlocksParams {
  search?: string;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
  sortBy?: 'name' | 'startDate' | 'endDate' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface GetBlockStatsParams {
  startDate?: string;
  endDate?: string;
}

export interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface BlockStats {
  totalBlocks: number;
  activeBlocks: number;
  inactiveBlocks: number;
  blocksByMonth: Array<{
    month: string;
    count: number;
  }>;
  upcomingBlocks: number;
  expiredBlocks: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp?: string;
}

export interface GetBlocksResponse {
  blocks: BlockWithStats[];
  pagination: PaginationMeta;
}

export interface GetBlocksForSelectionResponse {
  blocks: Array<{
    id: string;
    name: string;
    startDate: string;
    endDate: string;
  }>;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get all blocks with filtering and pagination
 * @param params - Query parameters
 * @returns Promise with blocks data
 */
export async function getBlocks(params: GetBlocksParams = {}, token: string): Promise<ApiResponse<GetBlocksResponse>> {

  const searchParams = new URLSearchParams();
  
  if (params.search) searchParams.append('search', params.search);
  if (params.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());
  if (params.startDate) searchParams.append('startDate', params.startDate);
  if (params.endDate) searchParams.append('endDate', params.endDate);
  if (params.sortBy) searchParams.append('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
  if (params.page) searchParams.append('page', params.page.toString());
  if (params.limit) searchParams.append('limit', params.limit.toString());

  const url = `${API_BASE_URL}/blocks?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: createHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Get block by ID
 * @param id - Block ID
 * @returns Promise with block data
 */
export async function getBlockById(id: string, token: string): Promise<ApiResponse<BlockWithStats>> {

  const response = await fetch(`${API_BASE_URL}/blocks/${id}`, {
    method: 'GET',
    headers: createHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Create a new block
 * @param data - Block creation data
 * @returns Promise with created block
 */
export async function createBlock(data: CreateBlockData, token: string): Promise<ApiResponse<BlockWithStats>> {

  const response = await fetch(`${API_BASE_URL}/blocks`, {
    method: 'POST',
    headers: createHeaders(token),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Update an existing block
 * @param id - Block ID
 * @param data - Block update data
 * @returns Promise with updated block
 */
export async function updateBlock(id: string, data: UpdateBlockData, token: string): Promise<ApiResponse<BlockWithStats>> {

  const response = await fetch(`${API_BASE_URL}/blocks/${id}`, {
    method: 'PUT',
    headers: createHeaders(token),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Delete a block
 * @param id - Block ID
 * @returns Promise with deletion result
 */
export async function deleteBlock(id: string, token: string): Promise<ApiResponse<{ message: string }>> {

  const response = await fetch(`${API_BASE_URL}/blocks/${id}`, {
    method: 'DELETE',
    headers: createHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Get block statistics
 * @param params - Query parameters
 * @returns Promise with block statistics
 */
export async function getBlockStats(params: GetBlockStatsParams = {}, token: string): Promise<ApiResponse<BlockStats>> {

  const searchParams = new URLSearchParams();
  
  if (params.startDate) searchParams.append('startDate', params.startDate);
  if (params.endDate) searchParams.append('endDate', params.endDate);

  const url = `${API_BASE_URL}/blocks/stats?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: createHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Get blocks for selection (dropdown)
 * @returns Promise with blocks for selection
 */
export async function getBlocksForSelection(token: string): Promise<ApiResponse<GetBlocksForSelectionResponse>> {

  const response = await fetch(`${API_BASE_URL}/blocks/selection`, {
    method: 'GET',
    headers: createHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format block duration
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Formatted duration string
 */
export function formatBlockDuration(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) {
    return '1 day';
  } else if (diffDays < 7) {
    return `${diffDays} days`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  } else {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? '1 month' : `${months} months`;
  }
}

/**
 * Get block status color
 * @param isActive - Whether block is active
 * @returns Color string for UI
 */
export function getBlockStatusColor(isActive: boolean): string {
  return isActive ? 'green' : 'red';
}

/**
 * Check if block is upcoming
 * @param startDate - Block start date
 * @returns True if block is upcoming
 */
export function isBlockUpcoming(startDate: string): boolean {
  return new Date(startDate) > new Date();
}

/**
 * Check if block is expired
 * @param endDate - Block end date
 * @returns True if block is expired
 */
export function isBlockExpired(endDate: string): boolean {
  return new Date(endDate) < new Date();
}

/**
 * Check if block is current (active and within date range)
 * @param startDate - Block start date
 * @param endDate - Block end date
 * @param isActive - Whether block is active
 * @returns True if block is current
 */
export function isBlockCurrent(startDate: string, endDate: string, isActive: boolean): boolean {
  if (!isActive) return false;
  
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return now >= start && now <= end;
}
